import { call } from '@advanza/api'
import { useCallback, useEffect, useState } from 'react'
import {
    CHATBOT_KNOWLEDGE_ENDPOINT,
    CRAWL_POLL_INTERVAL_MS,
    REQUEST_CHATBOT_KNOWLEDGE_ENDPOINT,
} from './chatbotKnowledgeConstants'
import type { ChatbotKnowledgeIndex, GetChatbotKnowledgeIndexResponse } from './types'

interface ChatbotKnowledgeIndexState {
    data: ChatbotKnowledgeIndex | null
    isLoading: boolean
    error: string | null
    /** Asks the Supply Agent for a fresh crawl and adopts the resulting state. */
    requestCrawl: () => Promise<void>
    isRequesting: boolean
    requestError: string | null
}

const readError = (error: unknown, fallback: string): string => {
    const candidate = error as { error?: string; message?: string } | null

    return candidate?.error || candidate?.message || fallback
}

export const useChatbotKnowledgeIndex = (providerId: number): ChatbotKnowledgeIndexState => {
    const [data, setData] = useState<ChatbotKnowledgeIndex | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isRequesting, setIsRequesting] = useState(false)
    const [requestError, setRequestError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        setData(null)
        setIsLoading(true)
        setError(null)
        setRequestError(null)

        call(`${CHATBOT_KNOWLEDGE_ENDPOINT}/${providerId}`)
            .then((response: GetChatbotKnowledgeIndexResponse) => {
                if (!cancelled) {
                    setData(response.knowledgeIndex ?? null)
                    setIsLoading(false)
                }
            })
            .catch((caught: unknown) => {
                if (!cancelled) {
                    setError(readError(caught, 'Failed to load knowledge index'))
                    setIsLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [providerId])

    // A crawl takes minutes, and the backend refreshes an in-progress row from the
    // agent on read, so polling is what turns "Crawling" into the real outcome
    // while the admin is still looking at the panel. Stops as soon as it settles.
    const isCrawling = data?.isCrawling === true

    useEffect(() => {
        if (!isCrawling) {
            return
        }

        let cancelled = false
        const timer = setInterval(() => {
            call(`${CHATBOT_KNOWLEDGE_ENDPOINT}/${providerId}`)
                .then((response: GetChatbotKnowledgeIndexResponse) => {
                    if (!cancelled) {
                        setData(response.knowledgeIndex ?? null)
                    }
                })
                .catch(() => {
                    // A failed poll is not worth surfacing: the next one may work,
                    // and the panel is already showing the last known state.
                })
        }, CRAWL_POLL_INTERVAL_MS)

        return () => {
            cancelled = true
            clearInterval(timer)
        }
    }, [isCrawling, providerId])

    const requestCrawl = useCallback(async () => {
        setIsRequesting(true)
        setRequestError(null)

        try {
            const response: GetChatbotKnowledgeIndexResponse = await call(
                `${REQUEST_CHATBOT_KNOWLEDGE_ENDPOINT}/${providerId}`,
                { method: 'post' },
            )
            // The endpoint returns the row it just wrote, so the panel reflects the
            // queued crawl without a second round trip.
            setData(response.knowledgeIndex ?? null)
        } catch (caught: unknown) {
            setRequestError(readError(caught, 'Could not start the crawl'))
        } finally {
            setIsRequesting(false)
        }
    }, [providerId])

    return { data, isLoading, error, requestCrawl, isRequesting, requestError }
}
